import React from 'react';
import { Shield, ShieldAlert } from 'lucide-react';

/**
 * Truth Gate Badge — DIR-000 visualized.
 * Renders a tiny pill under each Aria reply showing:
 *   - ✓ N — settled truth (score ≥ 95)
 *   - ⚠ N — surfaced uncertainty (score < 95) with hover-revealed advisory
 *
 * Also surfaces EchoCore severity if it isn't "calm":
 *   - drift  → amber dot
 *   - severe → red dot + NEXRECAL note
 *
 * Pure presentational. No network. Per DIR-057 Radical Transparency,
 * users can SEE Aria's confidence rather than trust it blindly.
 */
export const TruthGateBadge = ({ cathedral }) => {
  if (!cathedral || !cathedral.truth_gate) return null;

  const tg = cathedral.truth_gate || {};
  const ec = cathedral.echocore || {};
  const settled = !!tg.settled;
  const score = typeof tg.score === 'number' ? tg.score : null;
  const advisory = tg.advisory;
  const severity = ec.severity || 'calm';

  const color = settled ? '#00ff88' : '#ffb84d';
  const borderColor = settled ? 'rgba(0, 255, 136, 0.55)' : 'rgba(255, 184, 77, 0.55)';
  const bg = settled ? 'rgba(0, 255, 136, 0.10)' : 'rgba(255, 184, 77, 0.10)';
  const Icon = settled ? Shield : ShieldAlert;

  // EchoCore severity dot color
  const sevColor =
    severity === 'severe' ? '#ff5577'
    : severity === 'drift' ? '#ffb84d'
    : '#00ff88';

  // Tooltip explains DIR-000 plus current score and any drift notes
  const tooltip = [
    settled
      ? `DIR-000 Truth Gate: ${score?.toFixed(0)}/100 — settled.`
      : `DIR-000 Truth Gate: ${score?.toFixed(0)}/100 — below 95.`,
    advisory || '',
    severity !== 'calm'
      ? `EchoCore: ${severity}${Array.isArray(ec.notes) && ec.notes.length ? ` (${ec.notes.join(', ')})` : ''}`
      : '',
  ].filter(Boolean).join('\n\n');

  return (
    <div
      data-testid="truth-gate-badge"
      title={tooltip}
      style={{
        marginTop: '0.35rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.18rem 0.55rem',
        borderRadius: '999px',
        border: `1px solid ${borderColor}`,
        background: bg,
        color,
        fontSize: '0.65rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      <Icon size={11} style={{ flexShrink: 0 }} />
      <span data-testid="truth-gate-score">
        {settled ? '✓' : '⚠'} {score !== null ? Math.round(score) : '—'}
      </span>
      {severity !== 'calm' && (
        <span
          data-testid="echocore-severity-dot"
          aria-label={`EchoCore ${severity}`}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: sevColor,
            boxShadow: `0 0 6px ${sevColor}`,
          }}
        />
      )}
    </div>
  );
};

export default TruthGateBadge;
